// ANGEL FLIGHT — EconomySystem: combo multiplier + SCRIP/SALVAGE payout.
//
// On every kill: bump combo, award SCRIP = bounty × combo × difficulty payout
// (headshots pay a bonus); elites drop SALVAGE. Taking a hit breaks the combo.
// The math is the pure code in config/economy.ts.

import { BALANCE } from '../config/balance';
import { getDifficulty } from '../config/difficulty';
import { getEnemy } from '../config/enemies';
import {
  registerKill,
  tickCombo,
  breakCombo,
  comboMult,
  scripForKillZoned,
  salvageReward,
} from '../config/economy';
import type { EnemyId } from '../core/types';
import type { GameContext, GameSystem } from './System';

export class EconomySystem implements GameSystem {
  private ctx!: GameContext;

  private readonly onKill = (p: { def: EnemyId; bounty: number; headshot: boolean }): void => {
    const ctx = this.ctx;
    const diff = getDifficulty(ctx.difficulty);
    const mult = registerKill(ctx.run.combo);
    ctx.run.kills += 1;
    ctx.run.bestCombo = Math.max(ctx.run.bestCombo, ctx.run.combo.count);

    const scrip = scripForKillZoned(p.bounty, ctx.run.combo, diff, p.headshot);
    ctx.run.scrip += scrip;
    ctx.bus.emit('scrip:changed', { total: ctx.run.scrip, delta: scrip });
    ctx.bus.emit('combo:changed', { mult, count: ctx.run.combo.count });

    // SALVAGE is now data-driven off the enemy def (def.salvage), so any heavy
    // threat — today's elites, tomorrow's boss — funds the workshop without a
    // special-case in this system.
    const drop = getEnemy(p.def).salvage ?? 0;
    if (drop > 0) {
      const sal = salvageReward(drop, diff);
      ctx.run.salvage += sal;
      ctx.bus.emit('salvage:changed', { total: ctx.run.salvage, delta: sal });
    }
  };

  private readonly onHurt = (): void => {
    breakCombo(this.ctx.run.combo);
    this.ctx.bus.emit('combo:changed', { mult: comboMult(this.ctx.run.combo), count: 0 });
  };

  /** Shooting a BLUEFOR friendly is a PENALTY: break the combo (target discipline
   *  matters) AND dock SCRIP (clamped at zero so it never goes negative). The run
   *  counter + mission-fail check live in FiringSystem; this is the economy hit. */
  private readonly onFriendlyHit = (): void => {
    const ctx = this.ctx;
    breakCombo(ctx.run.combo);
    ctx.bus.emit('combo:changed', { mult: comboMult(ctx.run.combo), count: 0 });

    const penalty = Math.min(BALANCE.friendlyFire.scripPenalty, ctx.run.scrip);
    if (penalty > 0) {
      ctx.run.scrip -= penalty;
      ctx.bus.emit('scrip:changed', { total: ctx.run.scrip, delta: -penalty });
    }
  };

  init(ctx: GameContext): void {
    this.ctx = ctx;
    ctx.bus.on('enemy:killed', this.onKill);
    ctx.bus.on('hull:damaged', this.onHurt);
    ctx.bus.on('friendly:hit', this.onFriendlyHit);
  }

  update(dt: number): void {
    if (tickCombo(this.ctx.run.combo, dt)) {
      this.ctx.bus.emit('combo:changed', { mult: 1, count: 0 });
    }
  }

  destroy(): void {
    this.ctx?.bus.off('enemy:killed', this.onKill);
    this.ctx?.bus.off('hull:damaged', this.onHurt);
    this.ctx?.bus.off('friendly:hit', this.onFriendlyHit);
  }
}
